<template>
  <Button
    class="assistant-button tw:absolute tw:right-(--page-gutter) tw:size-[60px] tw:border-[3.5px] tw:border-solid tw:border-(--primary-foreground)"
    size="icon"
    :aria-label="t('nkf.ai')"
    @click="openAssistant"
    ><AppIcon size="dialog" name="sparkle" /><span
      class="tw:type-micro tw:absolute tw:-right-1.25 tw:-top-1.25 tw:grid tw:place-items-center tw:min-w-6 tw:h-5 tw:py-0 tw:px-1 tw:rounded-(--radius-full) tw:bg-(--orange) tw:text-foreground tw:border-[2px] tw:border-solid tw:border-(--primary-foreground)"
      aria-hidden="true"
      >AI</span
    ></Button
  >
  <AppDialog
    v-model:open="open"
    full-screen
    :title="t('nkf.assistant.title')"
    :description="t('nkf.assistant.status')"
  >
    <template #description
      ><span
        class="assistant-status tw:flex tw:items-center tw:text-(--success) tw:gap-1.25 tw:font-bold"
        >{{ t('nkf.assistant.status') }}</span
      ></template
    >
    <template #leading
      ><span
        class="assistant-heading__bot tw:grid tw:place-items-center tw:flex-none tw:text-primary-foreground tw:w-11.5 tw:h-11.5 tw:rounded-(--control-radius)"
        ><AppIcon size="card" name="sparkle" /></span
    ></template>
    <ol class="assistant-messages tw:flex tw:flex-col tw:gap-2.75" aria-live="polite">
      <li
        v-for="message in preview.assistantMessages"
        :key="message.id"
        class="assistant-message tw:self-start tw:bg-(--card) tw:shadow-(--shadow-soft) tw:wrap-anywhere tw:type-body tw:py-3 tw:px-3.75 tw:rounded-(--radius-card)"
        :class="{ 'assistant-message--me': message.me }"
      >
        <template v-if="message.me">{{ message.text }}</template>
        <template v-else>
          <template v-for="(part, index) in textParts(message)" :key="index"
            ><strong v-if="index % 2">{{ part }}</strong
            ><template v-else>{{ part }}</template></template
          >
          <div
            v-if="message.actions.length"
            class="assistant-actions tw:flex tw:flex-wrap tw:gap-2 tw:mt-2.5"
          >
            <template v-for="action in message.actions" :key="action.labelKey">
              <Button
                v-if="action.call"
                size="sm"
                :variant="action.variant"
                :disabled="!canCallTarget(action.call)"
                class="assistant-action tw:type-button-small tw:min-h-8 tw:py-0 tw:px-3.25"
                @click="callFromReply(action)"
                >{{ t(action.labelKey) }}</Button
              >
              <Button
                v-else
                as-child
                size="sm"
                :variant="action.variant"
                class="assistant-action tw:type-button-small tw:min-h-8 tw:py-0 tw:px-3.25"
                ><RouterLink :to="action.to" @click="open = false">{{
                  t(action.labelKey)
                }}</RouterLink></Button
              >
            </template>
          </div>
        </template>
      </li>
      <li
        v-if="replying"
        class="assistant-message assistant-typing tw:self-start tw:bg-(--card) tw:shadow-(--shadow-soft) tw:wrap-anywhere tw:flex tw:type-body tw:py-3.5 tw:px-4 tw:rounded-(--radius-card) tw:gap-1"
        role="status"
      >
        <span class="tw:sr-only">{{ t('nkf.assistant.typing') }}</span
        ><i
          class="tw:w-1.75 tw:h-1.75 tw:rounded-[50%] tw:bg-(--placeholder)"
          aria-hidden="true"
        ></i
        ><i
          class="tw:w-1.75 tw:h-1.75 tw:rounded-[50%] tw:bg-(--placeholder)"
          aria-hidden="true"
        ></i
        ><i
          class="tw:w-1.75 tw:h-1.75 tw:rounded-[50%] tw:bg-(--placeholder)"
          aria-hidden="true"
        ></i>
      </li>
    </ol>
    <div ref="conversationEnd" aria-hidden="true"></div>
    <template #footer
      ><div class="assistant-suggestions tw:flex tw:overflow-x-auto tw:gap-2">
        <Button
          v-for="chip in suggestions"
          :key="chip.labelKey"
          variant="outline"
          size="sm"
          :disabled="replying"
          @click="send(t(chip.labelKey), chip.reply)"
          >{{ t(chip.labelKey) }}</Button
        >
      </div>
      <p class="assistant-disclaimer tw:text-(--subtle-foreground) tw:text-center tw:type-micro">
        {{ t('nkf.assistant.disclaimer') }}
      </p>
      <form
        :id="formId"
        class="assistant-compose tw:flex tw:gap-2.5"
        @submit.prevent="send(question)"
      >
        <Input
          v-model="question"
          :maxlength="FORM_LIMIT.CHAT"
          :placeholder="t('nkf.assistant.placeholder')"
          :aria-label="t('nkf.assistant.placeholder')"
        /><Button
          type="submit"
          size="icon"
          :disabled="!question.trim() || replying"
          :aria-label="t('nkf.assistant.send')"
          ><AppIcon size="control" name="send"
        /></Button>
      </form>
    </template>
  </AppDialog>
</template>
<script setup>
// 1. Import necessary components
import { ref, computed, useId, watch, nextTick, onScopeDispose } from 'vue';
import { useRoute, RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { FORM_LIMIT } from '@/shared/constants/forms';
import {
  ASSISTANT_REPLY_DELAY_MS,
  ASSISTANT_DEFAULT_SUGGESTIONS,
  ASSISTANT_TAB_SUGGESTIONS,
} from '@/shared/constants/assistant';
import { findAssistantReply, matchAssistantReply } from '@/shared/utilities/assistant-replies';
import { useCallSheet } from '@/shared/composables/useCallSheet';
import { useAuthStore } from '@/core/stores/auth.store';
import { usePreviewStore } from '@/core/stores/preview.store';
import { Button } from './ui/button';
import AppDialog from './AppDialog.vue';
import AppIcon from './AppIcon.vue';
import { Input } from './ui/input';
// 2. Use
const { t } = useI18n();
const formId = useId();
const route = useRoute();
const auth = useAuthStore();
const preview = usePreviewStore();
const { callTarget, canCallTarget } = useCallSheet();
// 3. Define a reactive variable
const open = ref(false);
const question = ref('');
const replying = ref(false);
const conversationEnd = ref(null);
let replyTimer;
// 4. Define methods or computed properties if needed
// 4.1 Define methods
function addBotMessage(key, params, actions) {
  preview.assistantMessages.push({ id: crypto.randomUUID(), me: false, key, params, actions });
}
function openAssistant() {
  const page = t('nkf.' + route.meta.tab);

  // Tell a returning patient which page the conversation continues on.
  if (preview.assistantMessages.length && preview.assistantContext !== route.meta.tab) {
    addBotMessage('nkf.assistant.nowOn', { page }, []);
  }
  preview.assistantContext = route.meta.tab;

  if (!preview.assistantMessages.length) {
    const name = auth.userDisplayName.split(' ')[0];
    addBotMessage('nkf.assistant.greeting', { name, page }, []);
  }
  open.value = true;
}
// A reply id from a suggestion chip skips keyword matching.
function send(text, replyId) {
  const trimmed = text.trim();
  if (!trimmed || replying.value) {
    return;
  }

  let rule;
  if (replyId) {
    rule = findAssistantReply(replyId);
  } else {
    rule = matchAssistantReply(trimmed);
  }

  preview.assistantMessages.push({ id: crypto.randomUUID(), me: true, text: trimmed });
  question.value = '';
  replying.value = true;

  // The reply is stored even when the patient has left this page, so it is there on return.
  replyTimer = setTimeout(() => {
    addBotMessage('nkf.assistant.replies.' + rule.reply, {}, rule.actions);
    replying.value = false;
  }, ASSISTANT_REPLY_DELAY_MS);
}
// The conversation closes so the call sheet is the only prompt on screen.
function callFromReply(action) {
  open.value = false;
  callTarget(action.call);
}
// Reply text marks bold phrases with ** so translations keep their emphasis.
function textParts(message) {
  return t(message.key, message.params).split('**');
}
// 4.2 Define computed properties
const suggestions = computed(
  () => ASSISTANT_TAB_SUGGESTIONS[route.meta.tab] ?? ASSISTANT_DEFAULT_SUGGESTIONS,
);
// 5. VueJS Hooks
onScopeDispose(() => clearTimeout(replyTimer));
watch(
  [() => preview.assistantMessages.length, replying, open],
  async () => {
    await nextTick();
    conversationEnd.value?.scrollIntoView({ block: 'end' });
  },
  { flush: 'post' },
);
</script>
<style scoped>
.assistant-status::before {
  content: '';
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--success);
}
.assistant-heading__bot {
  background: linear-gradient(145deg, var(--gradient-info-start), var(--violet));
  box-shadow: 0 8px 18px color-mix(in srgb, var(--gradient-violet-start) 34.9%, transparent);
}
.assistant-suggestions {
  padding: 6px var(--page-gutter) 8px;
  scrollbar-width: none;
}
.assistant-suggestions :deep(button) {
  font: var(--type-secondary-strong);
  white-space: nowrap;
  flex: none;
  min-height: 36px;
  padding: 0 14px;
  border-radius: var(--radius-full);
  border: 1.5px solid var(--border-button);
  color: var(--link);
  letter-spacing: 0;
}
.assistant-message {
  max-width: 84%;
  border-end-start-radius: 6px;
}
.assistant-message--me {
  align-self: flex-end;
  border-end-start-radius: 20px;
  border-end-end-radius: 6px;
  background: var(--primary);
  color: var(--primary-foreground);
  box-shadow: none;
}
.assistant-typing i {
  animation: assistant-typing-dot 1s infinite;
}
.assistant-typing i:nth-child(2) {
  animation-delay: 0.15s;
}
.assistant-typing i:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes assistant-typing-dot {
  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: none;
  }
  30% {
    opacity: 1;
    transform: translateY(-3px);
  }
}
.assistant-compose {
  padding: 8px var(--page-gutter) 0;
}
.assistant-compose :deep(input) {
  flex: 1;
  min-width: 0;
  height: 50px;
  padding-inline: 18px;
  border-color: var(--border-measurement);
  border-radius: var(--radius-full);
  background: var(--card);
}
.assistant-compose button {
  width: 50px;
  height: 50px;
  box-shadow: 0 8px 18px color-mix(in srgb, var(--primary) 30.2%, transparent);
}
.assistant-compose button:disabled {
  opacity: 1;
}
.assistant-disclaimer {
  padding: 0 var(--page-gutter) 4px;
}
.assistant-button {
  z-index: 30;
  bottom: calc(var(--app-navigation-bottom) + var(--app-navigation-height) + 0.75rem);
  background: linear-gradient(
    145deg,
    var(--gradient-assistant-start),
    var(--gradient-assistant-end)
  );
  box-shadow: 0 12px 26px color-mix(in srgb, var(--gradient-assistant-end) 42%, transparent);
}
.assistant-button span {
  box-sizing: content-box;
}
</style>
